import React, { useContext, useEffect, useMemo, useState } from 'react';
import { Modal } from 'antd';
import service from '../utils/request';
import MailGroupModal from './MailGroupModal';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faMagnifyingGlass, faPenToSquare, faTrash, faBan, faPowerOff } from "@fortawesome/free-solid-svg-icons";
import message from "../utils/message";
import { SpaceContext } from '../SpaceContext';

const MailGroupManagement = () => {
	const [searchTerm, setSearchTerm] = useState('');
	const [emailAliases, setEmailAliases] = useState([]);
	const { userProfile } = useContext(SpaceContext);
	const [loading, setLoading] = useState(false);
	const [isModalOpen, setIsModalOpen] = useState(false);
	const [editData, setEditData] = useState(null);
	const [deleteModalVisible, setDeleteModalVisible] = useState(false);
	const [deleteItemId, setDeleteItemId] = useState(null);
	const [disableModalVisible, setDisableModalVisible] = useState(false);
	const [disableItemId, setDisableItemId] = useState(null);
	const [userInfo, setUserInfo] = useState(null);

	useEffect(() => {
		fetchEmailAliases();
		const storedUserInfo = localStorage.getItem('user_info');
		if (storedUserInfo) {
			setUserInfo(JSON.parse(storedUserInfo));
		}
	}, []);

	const fetchEmailAliases = async () => {
		try {
			setLoading(true);
			const response = await service.get('/mailgroup/aliases');
			if (Array.isArray(response)) {
				const formattedAliases = response.map(alias => {
					let forwardTo = [];
					let isDisabled = false;

					if (alias.forward_addr) {
						if (alias.forward_addr.startsWith('DISABLED:')) {
							isDisabled = true;
							forwardTo = alias.forward_addr.substring(9).split(',').filter(addr => addr.trim());
						} else {
							forwardTo = alias.forward_addr.split(',').filter(addr => addr.trim());
						}
					}

					return {
						id: alias.id,
						email: alias.username,
						forwardTo: forwardTo,
						isDisabled: isDisabled,
						type: alias.alias_type === 2 ? "StandardGroupMail"
							: alias.alias_type === 3 ? "Special Alias"
								: alias.alias_type === 4 ? "Share Mailbox"
									: "StandardGroupMail",
						aliasType: alias.alias_type,
						isPersonal: alias.personal,
						isPublic: alias.public_allow,
						...alias
					};
				});
				setEmailAliases(formattedAliases);
			} else {
				setEmailAliases([]);
			}
		} catch (error) {
			message('error', error);
			setEmailAliases([]);
		} finally {
			setLoading(false);
		}
	};

	const handleAddAlias = () => {
		setEditData(null);
		setIsModalOpen(true);
	};

	const handleEdit = async (alias) => {
		setEditData(alias);
		setIsModalOpen(true);
	};

	const handleDelete = (aliasId) => {
		setDeleteItemId(aliasId);
		setDeleteModalVisible(true);
	};

	const handleDisable = (aliasId) => {
		setDisableItemId(aliasId);
		setDisableModalVisible(true);
	};

	const confirmDelete = async () => {
		try {
			await service.delete(`/mailgroup/delete_alias/${deleteItemId}`);
			message('success', 'Email alias deleted successfully');
			fetchEmailAliases();
			setDeleteModalVisible(false);
			setDeleteItemId(null);
		} catch (error) {
			message('error', error);
			console.error('Error deleting alias:', error);
		}
	};

	const confirmDisable = async () => {
		try {
			// Use the existing disable_alias endpoint
			await service.post(`/mailgroup/disable_alias?id=${disableItemId}`);
			message('success', 'Mail group disabled successfully');
			setDisableModalVisible(false);
			setDisableItemId(null);
			await fetchEmailAliases(); // Refresh the list
		} catch (error) {
			message('error', 'Failed to disable mail group');
			console.error('Error disabling mail group:', error);
		}
	};

	const handleModalClose = () => {
		setIsModalOpen(false);
		setEditData(null);
		fetchEmailAliases();
	};

	const filteredAliases = useMemo(() => {
		if (!Array.isArray(emailAliases)) return [];

		return emailAliases.filter(alias => {
			if (!alias || typeof alias.email !== 'string') return false;

			const searchLower = searchTerm.toLowerCase();
			const emailMatch = alias.email.toLowerCase().includes(searchLower);
			const forwardToMatch = Array.isArray(alias.forwardTo) &&
				alias.forwardTo.some(email =>
					typeof email === 'string' && email.toLowerCase().includes(searchLower)
				);

			return emailMatch || forwardToMatch;
		});
	}, [emailAliases, searchTerm]);

	const renderMailGroupRow = (group) => {
		const isDisabled = group.isDisabled || (!group.forwardTo || group.forwardTo.length === 0 || group.forwardTo.every(addr => addr.trim() === ''));

		return (
			<tr
				key={group.id}
				className={`${isDisabled ? 'bg-red-50' : 'bg-white'} hover:bg-gray-50 border-b border-gray-200`}
			>
				<td className="px-6 py-4 text-sm text-left text-gray-800">
					{group.email}
				</td>
				<td className="px-6 py-4 text-sm text-left text-gray-800">
					{isDisabled ? (
						<div>
							<span className="text-red-500 italic">Disabled</span>
							{group.forwardTo && group.forwardTo.length > 0 && (
								<div className="text-gray-500 text-xs">
									Stored: {group.forwardTo.join(', ')}
								</div>
							)}
						</div>
					) : (
						group.forwardTo.join(', ')
					)}
				</td>
				<td className="px-6 py-4 text-sm text-left text-gray-800 whitespace-nowrap">
					{group.aliasType === 3 ? (
						"Special Alias"
					) : group.aliasType === 4 ? (
						<span className="text-yellow-500">Share MailBox</span>
					) : (
						""
					)}
				</td>
				<td className="px-6 py-4 text-sm text-left text-gray-800">
					{isDisabled ? (
						<div className="flex items-center gap-3">
							<span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
								<svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
									<path fillRule="evenodd" d="M13.477 14.89A6 6 0 015.11 6.524l8.367 8.368zm1.414-1.414L6.524 5.11a6 6 0 018.367 8.367zM18 10a8 8 0 11-16 0 8 8 0 0116 0z" clipRule="evenodd" />
								</svg>
								Disabled
							</span>
							{(userProfile.role_id >= 2 || group.creator_id === userProfile.user_id) && (
								<button
									onClick={() => handleEnable(group)}
									className="text-green-600 transition-colors hover:text-green-800"
									title="Enable email"
								>
									<FontAwesomeIcon icon={faPowerOff} className="w-5 h-5" />
								</button>
							)}
						</div>
					) : (
						(userProfile.role_id >= 2 || group.creator_id === userProfile.user_id) &&
						<div className="flex gap-4">
							<button
								onClick={() => handleEdit(group)}
								className="text-blue-600 transition-colors hover:text-blue-800"
								title="Edit email"
							>
								<FontAwesomeIcon icon={faPenToSquare} className="w-5 h-5" />
							</button>
							<button
								onClick={() => handleDisable(group.id)}
								className="text-orange-600 transition-colors hover:text-orange-800"
								title="Disable email"
							>
								<FontAwesomeIcon icon={faBan} className="w-5 h-5" />
							</button>
							<button
								onClick={() => handleDelete(group.id)}
								className="text-red-600 transition-colors hover:text-red-800"
								title="Delete email"
							>
								<FontAwesomeIcon icon={faTrash} className="w-5 h-5" />
							</button>
						</div>
					)}
				</td>
			</tr>
		);
	};

	const handleEnable = (group) => {
		// You might want to open a modal to let user select forward addresses
		// or use the last known forward addresses stored elsewhere
		setEditData(group);
		setIsModalOpen(true);
	};

	return (
		<div className="flex flex-col mt-[1px] max-h-full w-full py-2">
			<div className="items-center p-4 fixed bg-gray-100 w-full z-50">
				<h1 className="text-xl text-left font-semibold text-gray-800 mb-4">Mail Group Management</h1>

				<div className="flex items-center gap-4 w-full justify-between">
					{userProfile.role_id >= 2 && (
						<button
							onClick={handleAddAlias}
							className="bg-blue-500 text-white px-4 py-2 rounded-md text-sm hover:bg-blue-600 transition-colors">
							New Alias
						</button>
					)}
					<div className="relative mr-10 w-1/2">
						<div className="absolute inset-y-0 start-0 flex items-center ps-3 pointer-events-none">
							<FontAwesomeIcon icon={faMagnifyingGlass} className="text-gray-500" />
						</div>
						<input
							type="search"
							id="search"
							className="block w-full p-2 ps-10 text-sm text-gray-900 rounded-lg outline-none focus:shadow focus:ring-blue-500 focus:border-blue-500"
							placeholder="Search"
							value={searchTerm}
							onChange={(e) => setSearchTerm(e.target.value)}
						/>
					</div>
				</div>
			</div>

			<div className="px-4 fixed py-2 pt-[7rem] w-[calc(100%-2rem)]">
				{loading ? (
					<div className="text-center py-8">
						<div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
						<p className="mt-2 text-gray-600">Loading mail groups...</p>
					</div>
				) : (
					<div className="overflow-hidden bg-white rounded-lg shadow">
						<div className="overflow-auto max-h-[calc(100vh-10rem)]">
							<table className="w-full border-collapse">
								<thead>
									<tr className="sticky top-0 border-b border-gray-200 bg-gray-50 z-10">
										<th className="px-6 py-3 text-sm font-medium text-gray-700 text-left">
											Email Alias
										</th>
										<th className="px-6 py-3 text-sm font-medium text-gray-700 text-left">
											Forward To
										</th>
										<th className="px-6 py-3 text-sm font-medium text-gray-700 text-left">
											Type
										</th>
										<th className="px-6 py-3 text-sm font-medium text-gray-700 text-left">
											Actions
										</th>
									</tr>
								</thead>
								<tbody>
									{filteredAliases.map((item, index) => renderMailGroupRow(item, index))}
								</tbody>
							</table>
						</div>
					</div>
				)}
			</div>

			{/* Modals remain the same */}
			<MailGroupModal
				isOpen={isModalOpen}
				onClose={handleModalClose}
				editData={editData}
				allAliases={emailAliases}
			/>
			<Modal
				title="Confirm Delete"
				open={deleteModalVisible}
				onOk={confirmDelete}
				onCancel={() => setDeleteModalVisible(false)}
				okText="Delete"
				cancelText="Cancel"
				okButtonProps={{
					style: { backgroundColor: '#ef4444', borderColor: '#ef4444' }
				}}
			>
				<p>Are you sure you want to delete this email alias?</p>
			</Modal>
			<Modal
				title="Confirm Disable"
				open={disableModalVisible}
				onOk={confirmDisable}
				onCancel={() => setDisableModalVisible(false)}
				okText="Disable"
				cancelText="Cancel"
				okButtonProps={{
					style: { backgroundColor: '#f97316', borderColor: '#f97316' }
				}}
			>
				<p>Are you sure you want to disable this email alias?</p>
			</Modal>
		</div>
	);
};

export default MailGroupManagement;
